'use client';

import { useEffect, useRef, useState } from 'react';

interface FeatureItem {
  title?: string;
  desc: string;
  count?: number;
  suffix?: string;
  label?: string;
}

const features: FeatureItem[] = [
  { title: 'Pencarian Semantik', desc: 'Temukan pasal dalam 1 detik' },
  { count: 270000, suffix: '+', label: 'Peraturan', desc: 'Database hukum terlengkap' },
  { count: 4500, suffix: '+', label: 'Aturan Konsolidasi', desc: 'Naskah utuh, siap baca' },
  { count: 4000, suffix: '+', label: 'Putusan MK', desc: 'Analisis hukum mendalam' },
  { count: 49000, suffix: '+', label: 'Data Merek', desc: 'Informasi HKI terkini' },
];

function AnimatedCounter({
  value,
  suffix = '',
  duration = 1500,
  delay = 0,
}: {
  value: number;
  suffix?: string;
  duration?: number;
  delay?: number;
}) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;

          setTimeout(() => {
            const start = performance.now();

            const animate = (now: number) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - (1 - progress) * (1 - progress);
              const current = Math.floor(eased * value);
              setCount(current);

              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setCount(value);
              }
            };

            requestAnimationFrame(animate);
          }, delay);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [value, duration, delay]);

  return <span ref={ref}>{count.toLocaleString('id-ID')}{suffix}</span>;
}

export function FeaturesList() {
  return (
    <div className="hidden md:block bg-white py-6 border border-neutral-200 relative z-20 mt-8 mx-4 md:mx-12 lg:mx-auto max-w-7xl rounded-2xl shadow-sm">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 lg:gap-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex flex-col justify-center items-center text-center px-2 group"
            >
              <div className="text-fluid-sm lg:text-fluid-base font-bold text-neutral-900 mb-0.5 md:mb-1.5 transition-transform duration-300 group-hover:-translate-y-0.5">
                {feature.count !== undefined ? (
                  <>
                    <AnimatedCounter value={feature.count} suffix={feature.suffix} delay={index * 200} />
                    {' '}{feature.label}
                  </>
                ) : (
                  feature.title
                )}
              </div>
              <div className="text-fluid-xs lg:text-fluid-sm text-neutral-500 max-w-[250px] md:max-w-[200px] leading-relaxed transition-transform duration-300 group-hover:-translate-y-0.5">
                {feature.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
